import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Calendar, Download, ThumbsUp, ThumbsDown, Smartphone, Play, Globe, Apple } from "lucide-react";
import { Logo } from "../components/Logo";
import { buildIcs, downloadIcs, googleCalendarUrl, outlookCalendarUrl, formatSlotIst } from "../lib/calendar";
import { HotspotVideoPlayer } from "../components/HotspotVideoPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CP picks a date (next 7 days) then a time from the program's daily grid (IST).
const pad = (n) => String(n).padStart(2, "0");

const nextDays = (n) => {
  const out = [];
  const nowIst = new Date(Date.now() + 5.5 * 3600e3);
  for (let i = 0; i < n; i++) {
    const d = new Date(nowIst.getTime() + i * 864e5);
    out.push(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
  }
  return out;
};

const timeGrid = (start, end, gap) => {
  const [sh, sm] = String(start || "09:00").split(":").map(Number);
  const [eh, em] = String(end || "21:00").split(":").map(Number);
  const out = [];
  let t = sh * 60 + sm;
  const e = eh * 60 + em;
  while (t <= e) { out.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`); t += Math.max(5, gap || 30); }
  return out;
};

const dayLabel = (isoDate) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[dt.getUTCDay()]}, ${dt.getUTCDate()} ${months[dt.getUTCMonth()]}`;
};

// Times still in the future for a given date (only filters when date is today, IST).
const futureTimes = (isoDate, times) => {
  const nowIst = new Date(Date.now() + 5.5 * 3600e3);
  const today = `${nowIst.getUTCFullYear()}-${pad(nowIst.getUTCMonth() + 1)}-${pad(nowIst.getUTCDate())}`;
  if (isoDate !== today) return times;
  const nowMin = nowIst.getUTCHours() * 60 + nowIst.getUTCMinutes();
  return times.filter((t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m > nowMin; });
};

export default function Onboarding() {
  const { slug, token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // per-step answers { stepId: { qid: value }}
  const [stage, setStage] = useState("welcome"); // welcome | slot | steps | done

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/onboard/${slug}/${token}`);
        setData(r.data);
        // Always land on welcome — this link gets shared across the CP's whole team.
        setStage("welcome");
        try {
          const saved = JSON.parse(localStorage.getItem(`cpn_viewer_${token}`) || "null");
          if (saved?.name) setViewerName(saved.name);
        } catch {}
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invite link not found. Please check with your team.");
      } finally { setLoading(false); }
    })();
  }, [slug, token]);

  const firstIncompleteIdx = (v) => {
    const doneIds = new Set(v?.completed_steps || []);
    const idx = (data?.program?.steps || []).findIndex((s) => !doneIds.has(s.id));
    return idx < 0 ? 0 : idx;
  };

  const identify = async () => {
    const name = viewerName.trim();
    if (!name) { setNameErr("Please enter your name to begin."); return; }
    setNameErr("");
    try {
      let savedId = null;
      try { savedId = JSON.parse(localStorage.getItem(`cpn_viewer_${token}`) || "null")?.id; } catch {}
      const { data: v } = await axios.post(`${API}/onboard/${token}/identify`, { name, viewer_id: savedId });
      setViewer(v);
      setAnswers(v.answers || {});
      localStorage.setItem(`cpn_viewer_${token}`, JSON.stringify({ id: v.id, name: v.name }));
      // Always offer the choice screen (Begin now / pick a slot) — even on revisit
      // or after completion, since the link is shared across the CP's whole team.
      setStepIdx(firstIncompleteIdx(v));
      setStage("choice");    } catch (e) {
      setNameErr("Could not start right now. Please try again.");
    }
  };

  const beginNow = () => {
    setStepIdx(firstIncompleteIdx(viewer));
    setStage("steps");
  };

  const scheduleLater = () => {
    setPickedDate(null);
    setStage("slot");
  };

  const [pickedDate, setPickedDate] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [viewerName, setViewerName] = useState("");
  const [nameErr, setNameErr] = useState("");
  const [calSlot, setCalSlot] = useState(null);
  const dates = useMemo(() => nextDays(7), []);
  const times = useMemo(
    () => (data ? timeGrid(data.program.schedule_start, data.program.schedule_end, data.program.slot_gap_minutes) : []),
    [data],
  );

  const pickSlot = async (slot) => {
    await axios.post(`${API}/onboard/${token}/pick-slot`, { slot, viewer_id: viewer?.id });
    setData((d) => ({ ...d, invite: { ...d.invite, chosen_slot: slot } }));
    setViewer((v) => (v ? { ...v, chosen_slot: slot } : v));
    setCalSlot(slot); // open the calendar chooser (Google / Outlook / Apple)
  };

  const downloadAppleIcs = () => {
    try {
      const ics = buildIcs({
        title: `Onboarding · ${data.program.title}`,
        description: `Welcome ${data.invite.cp_name}! Your personal onboarding link:`,
        joinUrl: window.location.href,
        startIst: calSlot,
        durationMinutes: 20,
      });
      downloadIcs(`onboarding-${slug}`, ics);
    } catch (e) {
      console.error("ics generation failed", e);
    }
  };

  const calUrls = calSlot
    ? {
        google: googleCalendarUrl({
          title: `Onboarding · ${data.program.title}`,
          description: `Your personal onboarding link: ${window.location.href}`,
          joinUrl: window.location.href,
          startIst: calSlot,
          durationMinutes: 20,
        }),
        outlook: outlookCalendarUrl({
          title: `Onboarding · ${data.program.title}`,
          description: `Your personal onboarding link: ${window.location.href}`,
          joinUrl: window.location.href,
          startIst: calSlot,
          durationMinutes: 20,
        }),
      }
    : null;

  const continueToSteps = () => {
    setCalSlot(null);
    setStepIdx(firstIncompleteIdx(viewer));
    setStage("steps");
  };

  const completeStep = async (extraAnswers) => {
    const step = data.program.steps[stepIdx];
    const merged = extraAnswers ? { ...(answers[step.id] || {}), ...extraAnswers } : (answers[step.id] || undefined);
    await axios.post(`${API}/onboard/${token}/progress`, {
      step_id: step.id, completed: true, answers: merged, viewer_id: viewer?.id,
    });
    const nextCompleted = { ...answers, [step.id]: merged || {} };
    setAnswers(nextCompleted);
    if (stepIdx + 1 >= data.program.steps.length) setStage("done");
    else setStepIdx(stepIdx + 1);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (err) return <div className="min-h-screen flex items-center justify-center text-red-600 px-6 text-center">{err}</div>;

  const { invite, program } = data;

  return (
    <div className="min-h-screen">
      <header className="px-6 py-4"><Logo /></header>

      <div className="max-w-3xl mx-auto px-5 pb-16">
        {stage === "welcome" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-strong rounded-3xl p-8 mt-8" data-testid="onboard-welcome">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-bold mb-3">
              Personal onboarding · {program.estimated_minutes}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A] leading-tight">
              Welcome <span className="text-[#FF5E1A]">{invite.cp_company || invite.cp_name}</span> —
            </h1>
            <p className="text-[15px] md:text-base text-[#475569] mt-3 max-w-xl">
              Total time: {program.estimated_minutes}.
              {program.description && <> {program.description}</>}
            </p>
            <div className="mt-5 max-w-sm">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Your name</label>
              <input type="text" value={viewerName} onChange={(e) => setViewerName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && identify()}
                placeholder="Enter your name to begin"
                data-testid="onboard-name-input"
                className="mt-1 w-full px-4 py-3 rounded-2xl glass-input text-sm" />
              {nameErr && <div className="text-xs text-red-600 mt-1.5" data-testid="onboard-name-error">{nameErr}</div>}
            </div>
            <button onClick={identify} data-testid="onboard-pick-slot-cta"
              className="mt-4 rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-6 py-3 font-semibold cp-shadow-orange inline-flex items-center gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
            {data.viewers?.length > 0 && (
              <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-3 max-w-sm" data-testid="onboard-viewers">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">From this team so far</div>
                {data.viewers.map((v, i) => (
                  <div key={i} className="text-xs text-slate-600 flex justify-between py-0.5">
                    <span className="font-medium">{v.name}</span>
                    <span className={v.completed ? "text-emerald-600 font-semibold" : ""}>{v.completed ? "Completed" : `${v.progress_percent}% done`}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {stage === "choice" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-strong rounded-3xl p-8 mt-8" data-testid="onboard-choice">
            <h2 className="font-heading text-xl font-bold mb-1">Ready when you are{viewer?.name ? `, ${viewer.name}` : ""}</h2>
            <p className="text-sm text-slate-500 mb-5">Start right away, or book a future slot and get a calendar invite.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={beginNow} data-testid="onboard-begin-now"
                className="flex-1 rounded-2xl bg-[#0F172A] hover:bg-slate-800 text-white px-6 py-4 text-sm font-bold transition-colors">
                Begin now
              </button>
              <button onClick={scheduleLater} data-testid="onboard-schedule-later"
                className="flex-1 rounded-2xl bg-white border-2 border-[#FF5E1A] text-[#E04F00] hover:bg-[#FFF3ED] px-6 py-4 text-sm font-bold transition-colors inline-flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" /> Schedule for later
              </button>
            </div>
          </motion.div>
        )}

        {stage === "slot" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-strong rounded-3xl p-8 mt-8" data-testid="onboard-slot-picker">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-[#FF5E1A]" />
              <h2 className="font-heading text-xl font-bold">{pickedDate ? `Pick a time · ${dayLabel(pickedDate)}` : "Pick a date"}</h2>
            </div>
            {!pickedDate ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="onboard-date-grid">
                {dates.map((d) => (
                  <button key={d} onClick={() => setPickedDate(d)}
                    data-testid={`onboard-date-${d}`}
                    className="rounded-2xl bg-white border-2 border-slate-200 hover:border-[#FF5E1A] px-4 py-3 text-sm font-semibold transition-colors">
                    {dayLabel(d)}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="onboard-time-grid">
                  {futureTimes(pickedDate, times).map((t) => {
                    const iso = `${pickedDate}T${t}`;
                    return (
                      <button key={iso} onClick={() => pickSlot(iso)}
                        data-testid={`onboard-slot-${iso}`}
                        className="rounded-2xl bg-white border-2 border-slate-200 hover:border-[#FF5E1A] px-3 py-2.5 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1.5">
                        <Download className="w-3.5 h-3.5 text-[#FF5E1A]" /> {t}
                      </button>
                    );
                  })}
                  {futureTimes(pickedDate, times).length === 0 && (
                    <div className="col-span-full text-sm text-slate-500 py-3">No more slots today — please pick another date.</div>
                  )}
                </div>
                <button onClick={() => setPickedDate(null)} data-testid="onboard-back-dates" className="mt-4 text-xs text-slate-500 hover:text-[#FF5E1A] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Change date</button>
              </>
            )}
            {!pickedDate && (
              <button onClick={() => setStage("choice")} className="mt-4 text-xs text-slate-500 hover:text-[#FF5E1A] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</button>
            )}
          </motion.div>
        )}

        {stage === "steps" && program.steps[stepIdx] && (
          <StepView
            key={program.steps[stepIdx].id}
            invite={invite}
            program={program}
            step={program.steps[stepIdx]}
            stepIdx={stepIdx}
            totalSteps={program.steps.length}
            existing={answers[program.steps[stepIdx].id] || {}}
            onContinue={completeStep}
          />
        )}

        {stage === "done" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-strong rounded-3xl p-10 mt-8 text-center" data-testid="onboard-complete">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3"><Check className="w-8 h-8 text-emerald-600" /></div>
            <h2 className="font-heading text-2xl font-bold">Bahut badhiya, {viewer?.name || invite.cp_company || invite.cp_name}! 🎉</h2>
            <p className="text-[#475569] mt-2">Onboarding complete. Aapke team member ke saath jald hi baat hogi.</p>
          </motion.div>
        )}
      </div>

      {/* Calendar chooser after a future slot is picked */}
      {calSlot && calUrls && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center px-4" data-testid="onboard-calendar-modal">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl">
            <h3 className="font-heading font-bold text-lg">Add to your calendar</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">{formatSlotIst(calSlot)} · 20 min</p>
            <div className="flex flex-col gap-2">
              <a href={calUrls.google} target="_blank" rel="noopener noreferrer" data-testid="cal-google"
                className="rounded-2xl border-2 border-slate-200 hover:border-[#2A5CB8] px-4 py-3 text-sm font-semibold text-center transition-colors">
                Google Calendar
              </a>
              <a href={calUrls.outlook} target="_blank" rel="noopener noreferrer" data-testid="cal-outlook"
                className="rounded-2xl border-2 border-slate-200 hover:border-[#2A5CB8] px-4 py-3 text-sm font-semibold text-center transition-colors">
                Outlook
              </a>
              <button onClick={downloadAppleIcs} data-testid="cal-apple"
                className="rounded-2xl border-2 border-slate-200 hover:border-[#2A5CB8] px-4 py-3 text-sm font-semibold transition-colors">
                Apple / others (.ics download)
              </button>
            </div>
            <button onClick={continueToSteps} data-testid="cal-continue"
              className="mt-4 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-3 text-sm font-bold transition-colors">
              Continue to onboarding
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepView({ invite, program, step, stepIdx, totalSteps, existing, onContinue }) {
  const [local, setLocal] = useState(existing || {});
  const [errMsg, setErrMsg] = useState("");
  const [ivFinished, setIvFinished] = useState(false);

  const ivDone = step.kind !== "interactive_video" || !step.url || ivFinished;

  const canContinue = (() => {
    if (!ivDone) return false;
    if (step.kind !== "checkpoint") return true;
    for (const q of step.questions || []) {
      const v = local[q.id];
      if (v === undefined || v === null || v === "") return false;
      if (q.type === "thumbs_up" && v === "no") return false; // must be Yes
    }
    return true;
  })();

  const handleContinue = () => {
    setErrMsg("");
    if (step.kind === "checkpoint") {
      for (const q of step.questions || []) {
        const v = local[q.id];
        if (q.type === "thumbs_up" && v !== "yes") { setErrMsg("Please confirm you got it before continuing."); return; }
      }
    }
    onContinue(step.kind === "checkpoint" ? local : undefined);
  };

  const embed = (kind, url) => {
    if (!url) return <div className="rounded-2xl bg-slate-100 p-6 text-center text-slate-500 text-sm">No URL set.</div>;
    return (
      <div className="rounded-2xl overflow-hidden bg-slate-900" style={{ aspectRatio: "16/9" }}>
        <iframe src={url} title={step.title || step.kind} className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="mt-8" data-testid={`onboard-step-${stepIdx}`}>
      <div className="flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-wider text-[#FF5E1A]">
        Step {stepIdx + 1} of {totalSteps} · {step.title || step.kind}
      </div>
      <h2 className="font-heading text-2xl font-bold text-[#0F172A] mb-4">{step.title || (step.kind === "checkpoint" ? "Quick check" : "Watch")}</h2>

      {step.kind === "interactive_video" ? (
        <HotspotVideoPlayer
          url={step.url}
          hotspots={step.hotspots || []}
          description={step.description || ""}
          onFinished={() => setIvFinished(true)}
        />
      ) : step.kind === "app_link" ? (
        (() => {
          const linkList = (step.links || []).filter((l) => l.url);
          if (linkList.length === 0 && step.url) linkList.push({ id: "single", label: "Open the App", url: step.url });
          const iconFor = (label) => {
            const l = (label || "").toLowerCase();
            if (l.includes("play")) return <Play className="w-4 h-4" />;
            if (l.includes("apple") || l.includes("ios") || l.includes("app store")) return <Apple className="w-4 h-4" />;
            return <Globe className="w-4 h-4" />;
          };
          return (
            <div className="rounded-2xl bg-slate-900 p-6 sm:p-8 text-center" data-testid="app-link-step">
              <Smartphone className="w-10 h-10 text-[#FF5E1A] mx-auto mb-3" />
              <div className="text-white font-bold text-lg">Ab khud try karo</div>
              <p className="text-slate-300 text-sm mt-1 mb-4">Open the app and explore the same steps yourself.</p>
              {linkList.length > 0 ? (
                <div className="flex flex-col gap-2 max-w-xs mx-auto">
                  {linkList.map((l, i) => (
                    <a key={l.id || i} href={l.url} data-testid={`app-link-open-${i}`}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-6 py-3.5 text-sm font-bold transition-colors">
                      {iconFor(l.label)} {l.label || "Open the App"} <ArrowRight className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400 text-sm">App links not set yet.</div>
              )}
            </div>
          );
        })()
      ) : step.kind !== "checkpoint" && embed(step.kind, step.url)}

      {step.kind === "checkpoint" && (
        <div className="flex flex-col gap-4">
          {(step.questions || []).map((q) => (
            <div key={q.id} className="glass-strong rounded-2xl p-5" data-testid={`onboard-q-${q.id}`}>
              <div className="font-semibold text-[#0F172A] mb-3">{q.prompt || "Question"}</div>
              {q.type === "thumbs_up" && (
                <div className="flex gap-2">
                  <button onClick={() => setLocal({ ...local, [q.id]: "yes" })}
                    data-testid={`onboard-q-${q.id}-yes`}
                    className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold ${local[q.id] === "yes" ? "bg-emerald-600 text-white" : "bg-white border border-slate-300 text-slate-700"}`}>
                    <ThumbsUp className="w-4 h-4" /> Yes
                  </button>
                  <button onClick={() => setLocal({ ...local, [q.id]: "no" })}
                    data-testid={`onboard-q-${q.id}-no`}
                    className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold ${local[q.id] === "no" ? "bg-red-500 text-white" : "bg-white border border-slate-300 text-slate-700"}`}>
                    <ThumbsDown className="w-4 h-4" /> No, rewatch
                  </button>
                </div>
              )}
              {q.type === "mcq" && (
                <div className="flex flex-col gap-2">
                  {(q.options || []).map((opt, i) => (
                    <label key={i} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 cursor-pointer ${local[q.id] === String(i) ? "border-[#FF5E1A] bg-[#FFF3ED]" : "border-slate-200 bg-white"}`}>
                      <input type="radio" name={q.id} value={String(i)} checked={local[q.id] === String(i)} onChange={() => setLocal({ ...local, [q.id]: String(i) })} data-testid={`onboard-q-${q.id}-opt-${i}`} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === "short_text" && (
                <textarea value={local[q.id] || ""} onChange={(e) => setLocal({ ...local, [q.id]: e.target.value })}
                  placeholder="Your answer…" data-testid={`onboard-q-${q.id}-text`}
                  className="w-full px-3 py-2 rounded-xl glass-input text-sm min-h-[80px]" />
              )}
            </div>
          ))}
        </div>
      )}

      {errMsg && <div className="text-xs text-red-600 font-medium mt-3">{errMsg}</div>}
      <div className="flex justify-between items-center mt-6">
        <div className="text-xs text-slate-500">
          {step.kind === "interactive_video" && !ivDone ? "Watch the full video to continue" : "Progress auto-saved"}
        </div>
        <button onClick={handleContinue} disabled={!canContinue}
          data-testid="onboard-continue-btn"
          className="rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-6 py-3 font-semibold cp-shadow-orange inline-flex items-center gap-2 disabled:opacity-40">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
